import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, catchError, of, map } from 'rxjs';
import {
  SearchEquipmentResponse,
  Equipment,
  EquipmentGroupResponse,
  EquipmentRecommendation,
  ComparisonOutput,
  RentalSummary,
} from '../models/equipment.model';

export interface ChatMessage {
  role: 'user' | 'ai';
  text: string;
  timestamp: Date;
  equipmentResults?: Equipment[];
  searchMeta?: {
    location?: string;
    category?: string;
  };
}

/** Full response shape from the orchestrator */
export interface AiResponse {
  // ── Existing fields (unchanged) ──────────────────────────────────────────
  text: string;
  results?: Equipment[];
  intent?: string;
  equipmentGroups?: EquipmentGroupResponse[];
  parsed?: any;
  needsLocation?: boolean;
  followUpQuestion?: string;
  rentalSummary?: RentalSummary;
  recommendationSummary?: string;
  recommendedCount?: number;
  availableCount?: number;

  // ── Additive: richer orchestrator fields ──────────────────────────────────
  recommendations?: EquipmentRecommendation[];
  comparison?: ComparisonOutput;
  technicalAnswer?: string;
  assumptions?: string[];
}

@Injectable({ providedIn: 'root' })
export class AiService {
  private readonly functionUrl =
    'https://us-central1-alynmaster-6a727.cloudfunctions.net/searchEquipment';

  constructor(private http: HttpClient) {}

  sendMessage(userMessage: string, history: any[] = []): Observable<AiResponse> {
    return this.http
      .post<SearchEquipmentResponse>(this.functionUrl, { message: userMessage, history })
      .pipe(
        map((data: any): AiResponse => ({
          // Existing contract
          text: data.message,
          results: data.results,
          intent: data.intent,
          equipmentGroups: data.equipmentGroups,
          parsed: data.parsed,
          needsLocation: data.needsLocation,
          followUpQuestion: data.followUpQuestion,
          rentalSummary: data.rentalSummary,
          recommendationSummary: data.recommendationSummary,
          recommendedCount: data.recommendedCount,
          availableCount: data.availableCount,

          // Additive
          recommendations: data.recommendations,
          comparison: data.comparison,
          technicalAnswer: data.technicalAnswer,
          assumptions: data.assumptions,
        })),
        catchError((error) => {
          console.error('AiService error:', error);
          return of({
            text: "Sorry, I'm having trouble connecting right now. Please try again.",
            results: [],
          });
        })
      );
  }

  parseQuery(userMessage: string): Observable<any> {
    return this.http
      .post<SearchEquipmentResponse>(this.functionUrl, { message: userMessage, parseOnly: true })
      .pipe(
        map((data: any) => ({
          intent: data.intent,
          parsed: data.parsed,
        })),
        catchError((error) => {
          console.error('AiService parse error:', error);
          return of(null);
        })
      );
  }
}